import React from "react";
import { useTranslation } from "react-i18next";
import { ColumnCountField } from "./controls/ColumnCountField";
import { SettingSection } from "./controls/SettingSection";
import { Panel } from "./Panel";
import {
  SurfaceAppearanceSection,
  SurfaceCornerRoundingFields,
} from "./SurfaceAppearanceFields";
import { EmailBuilderSettingsPaddingSection } from "./controls/EmailBuilderSettingsPaddingSection";
import { ColumnLayoutSection } from "./ColumnLayoutSection";
import { ROW_SETTINGS_DEFAULT_EXPANDED } from "./settingsPanelDefaults";
import {
  isCornerRoundingInactive,
  isPaddingInactive,
  surfaceCornerRoundingAddDefault,
  surfacePaddingAddDefault,
} from "./settingsSectionActivation";
import {
  baselineRowBorder,
  baselineRowBorderRadius,
  baselineRowColumnGap,
  baselineRowPadding,
  baselineGroupedRowPadding,
  baselineRowBackgroundColor,
} from "../domain/baselines";
import { findRow, locateRow } from "../domain/find";
import { selectDocument } from "../domain/selectors";
import { useEditorActions, useEditorStore } from "../domain/redux/hooks";
import { EmailBuilderBoundedRange } from "../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";

type RowSettingsPanelProps = {
  rowId: string;
};

export function RowSettingsPanel({ rowId }: RowSettingsPanelProps) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const { updateRow } = useEditorActions();
  const row = findRow(document, rowId);
  if (!row) {
    return null;
  }

  const rowLocation = locateRow(document.sections, rowId);
  const nestedInGroup = rowLocation?.location.parent === "group";

  const defaultGap = baselineRowColumnGap(row.columns.length);
  const defaultPadding = nestedInGroup
    ? baselineGroupedRowPadding()
    : baselineRowPadding();
  const defaultBorder = baselineRowBorder();
  const defaultRadius = baselineRowBorderRadius();
  const appearanceBaselines = {
    backgroundColor: baselineRowBackgroundColor(),
    border: defaultBorder,
    borderRadius: defaultRadius,
  };

  return (
    <Panel
      title={t("Row settings")}
      defaultExpandedSectionIds={ROW_SETTINGS_DEFAULT_EXPANDED}
      ariaLabel={t("Row settings")}
    >
      <SettingSection sectionId="layout" title={t("Layout")}>
        <ColumnCountField rowId={row.id} />
        <ColumnLayoutSection rowId={row.id} />
        <EmailBuilderBoundedRange
          label={t("Space between columns (px)")}
          value={row.columnGap}
          min={0}
          max={40}
          defaultValue={defaultGap}
          onChange={columnGap => {
            if (columnGap === undefined) {
              return;
            }
            updateRow(row.id, { columnGap });
          }}
        />
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(row.padding),
          onAdd: () => updateRow(row.id, { padding: surfacePaddingAddDefault() }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Row padding (px)")}
          value={row.padding}
          defaultValue={defaultPadding}
          onChange={padding => {
            updateRow(row.id, { padding: { ...padding } });
          }}
        />
      </SettingSection>

      <SettingSection
        sectionId="corner-rounding"
        title={t("Corner rounding")}
        activation={{
          mode: "optional",
          isActive: !isCornerRoundingInactive(row.borderRadius),
          onAdd: () =>
            updateRow(row.id, { borderRadius: surfaceCornerRoundingAddDefault() }),
        }}
      >
        <SurfaceCornerRoundingFields
          value={row}
          baselines={appearanceBaselines}
          onChange={patch => updateRow(row.id, patch)}
        />
      </SettingSection>

      <SettingSection sectionId="appearance" title={t("Appearance")}>
        <SurfaceAppearanceSection
          value={row}
          baselines={appearanceBaselines}
          allowTransparentBackground
          showBackgroundImage
          onChange={patch => updateRow(row.id, patch)}
        />
      </SettingSection>
    </Panel>
  );
}
